// Updated Dropdown.jsx
import React, { useState, useRef, useEffect } from "react";
import arrowClose from "../../assets/kangaroo/arrowclose.png";
import "./dropdown.css";
import { ChevronDown, ChevronUp } from "lucide-react";
import nepal from "../../assets/kangaroo/nepalflag.png";

const Dropdown = ({
  items,
  placeholder = "Destination",
  arrowIcon,
  onSelect,
  selectedItem,
  setSelectedItem,
  width = "100%",
  phoneNumber,
  placeholderFlag = false,
  isUniversity = false,
  multiSelect = false

}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [selectedItems, setSelectedItems] = useState([]); // Store all selected items

  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (item) => {
    const isAlreadySelected = selectedItems.some(selected => selected.id === item.id);

    let newSelectedItems;
    if (isAlreadySelected) {
      // Remove item if already selected
      newSelectedItems = selectedItems.filter(selected => selected.id !== item.id);
    } else {
      // Add item to selected items
      newSelectedItems = [...selectedItems, item];
    }

    setSelectedItems(newSelectedItems);

    // Update selectedItem to the last selected item, or null if none selected
    if (newSelectedItems.length > 0) {
      const lastItem = newSelectedItems[newSelectedItems.length - 1];
      setSelectedItem(lastItem);
    } else {
      setSelectedItem(null);
    }

    // Update select all state
    setSelectAll(newSelectedItems.length === items.length);

    if (onSelect) onSelect(newSelectedItems);
  };

  // Handle select all toggle
  const handleSelectAll = () => {
    if (selectAll) {
      // Deselect all
      setSelectedItems([]);
      setSelectedItem(null);
      setSelectAll(false);
      if (onSelect) onSelect([]);
    } else {
      // Select all
      setSelectedItems([...items]);
      setSelectedItem(items[0]);
      setSelectAll(true);
      if (onSelect) onSelect([...items]);
    }
  };


  const isItemSelected = (item) => {
    return selectedItems.some(selected => selected.id === item.id);
  };

  // Extract country code from label (e.g., "Nepal +977" -> "+977")
  const getCountryCode = (label) => {
    const match = label.match(/\+\d+/);
    return match ? match[0] : "";
  };

  useEffect(() => {
    if (multiSelect && Array.isArray(selectedItem)) {
      setSelectedItems(selectedItem);
      setSelectAll(selectedItem?.length === items?.length);
    }
  }, [selectedItem, items, multiSelect]);

  // console.log("items selected:", selectedItems)
  // console.log("all items:", selectAll)

  return (
    <div
      className="dropdown-container"
      ref={dropdownRef}
      style={phoneNumber ? { width: "auto" } : { width }}
    >
      <button
        className="dropdown-button"
        onClick={() => setIsOpen(!isOpen)}
        style={
          phoneNumber
            ? {
              padding: "0.5rem 12px",
              borderTop: "1.55px solid #687C86",
              borderLeft: "1.55px solid #687C86",
              borderBottom: "1.55px solid #687C86",
              backgroundColor: "#d6dced",
              borderRight: "0",
              borderTopLeftRadius: "8px",
              borderBottomLeftRadius: "8px",
              borderTopRightRadius: "0",
              borderBottomRightRadius: "0",
              whiteSpace: "nowrap",
              width: "auto",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }
            : {
              width: width,
              border: "1.55px solid #687C86",
              borderBottom: isOpen
                ? "1.55px solid #687C86"
                : "1.55px solid #687C86",
              borderBottomLeftRadius: isOpen ? 0 : "8px",
              borderBottomRightRadius: isOpen ? 0 : "8px",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
            }
        }
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        type="button"
      >
        <span
          className="dropdown-label"
          style={{ display: "flex", alignItems: "center", gap: "8px" }}
        >
          {selectedItem ? (
            // When an item is selected
            <>
              {phoneNumber && selectedItem.flag && (
                <img
                  src={selectedItem.flag}
                  alt={selectedItem.label}
                  style={{
                    width: "24px",
                    height: "24px",
                    objectFit: "cover",
                    borderRadius: "50%",
                  }}
                />
              )}
              {placeholder}
            </>
          ) : (
            // When nothing is selected: placeholderFlag + placeholder
            <>
              {placeholderFlag && (
                <img
                  src={placeholderFlag}
                  alt="placeholder-flag"
                  style={{
                    width: "24px",
                    height: "24px",
                    objectFit: "cover",
                    borderRadius: "50%",
                  }}
                />
              )}
              {isUniversity ? <p className="font-level-7 my-auto">{placeholder} </p> : placeholder}
            </>
          )}
        </span>

        <div className={`dropdown-arrow ${isOpen ? "open" : ""}`}>
          <ChevronDown size="2.5rem" />
        </div>
      </button>

      {isOpen && (
        <ul
          className={`dropdown-menu ${isOpen ? "open" : "close"}`}
          style={
            phoneNumber
              ? {
                width: "100%",
                alignContent: "flex-start",
                padding: "0px",
                maxHeight: "300px",
                overflowY: "auto",
              }
              : {
                width: width,
                maxHeight: isMobile ? "300px" : "300px",
                overflowY: "auto",
              }
          }
        >
          {multiSelect && (
            <>
              <li>
                <button
                  type="button"
                  className="dropdown-item d-flex align-items-center gap-3 px-0"
                  onClick={handleSelectAll}
                >
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span>Select All</span>
                </button>
              </li>
              <li><hr className="dropdown-divider" /></li>
            </>
          )}

          {items.map((item, index) => (
            <li key={item.id}>
              <div className="d-flex justify-content-center align-items-center">
                {multiSelect && (
                  <input
                    type="checkbox"
                    checked={isItemSelected(item)}
                    onChange={() => handleSelect(item)}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                <button
                  className="py-2 dropdown-item"
                  onClick={() => handleSelect(item)}
                  role="option"
                  aria-selected={multiSelect ? isItemSelected(item) : selectedItem?.id === item.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    paddingLeft: phoneNumber ? "0px" : undefined,
                  }}
                >
                  {phoneNumber && item.flag && (
                    <img
                      src={item.flag}
                      alt={item.label}
                      style={{
                        width: "24px",
                        height: "24px",
                        objectFit: "cover",
                        borderRadius: "50%",
                      }}
                    />
                  )}
                  {item.label}
                </button>
              </div>
              {index < items.length - 1 && <div className="dropdown-divider" />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Dropdown;
